<template>
  <v-col cols="12">
    <v-col class="pa-0">
      <template v-if="productShoppingCart.length > 0">
        <v-row class="mb-7 mt-0">
          <v-chip class="my-1 mr-3" color="primary">
            <h3>1/3</h3>
          </v-chip>
          <h2
            :style="`color: ${$vuetify.theme.themes.light.primary}`"
            class="pl-2 pa-sm-0"
          >
            {{ $translate('CHOOSE_DELIVERY_PICKUP_METHOD') }}
          </h2>
        </v-row>

        <!-- Error banner -->
        <v-slide-y-transition>
          <ErrorCard
            v-if="error"
            :errorMessage="errorMessage"
            :functionName="errorToRecallFunction"
            @getLocations="getLocations"
            @getStartDates="getStartDates"
            @getStartBatches="getStartBatches"
            @getEndDates="getEndDates"
            @getDeliveryAndPickUpEvents="getDeliveryAndPickUpEvents"
          />
        </v-slide-y-transition>

        <v-alert
          v-if="succesfulStartDate === true"
          dense
          border="left"
          type="warning"
        >
          {{ $translate('ALERT_NO_DATES') }}
        </v-alert>

        <v-row justify="space-between" class="ma-0">
          <v-col cols="12" class="pa-0">
            <v-row>
              <v-col cols="12" md="6" v-if="allLocations.size > 0">
                <v-select
                  v-model="orderDetails.Location"
                  :label="$translate('CHOOSE_A_LOCATION')"
                  :items="allLocations"
                  item-text="LocationName"
                  :disabled="allLocations.length < 2"
                  return-object
                  append-icon="mdi-map-marker"
                  @change="getStartDates()"
                />
              </v-col>

              <!-- Delivery & PickUp options -->
              <v-col
                v-if="deliveryAndPickUpDataLoaded"
                class="pt-3 mb-3"
                cols="12"
                md="6"
              >
                <v-select
                  v-model="orderType"
                  :items="deliveryPickUpSelect"
                  hide-details="auto"
                  outlined
                  return-object
                  item-value="Name"
                  item-text="Name"
                  :prepend-inner-icon="
                    orderType.Type === 'Delivery' ? 'mdi-truck' : 'mdi-store'
                  "
                  :label="$translate('MAKE_CHOICE_FOR_DELIVERY_PICKUP')"
                  single-line
                  @change="updateDeliveryData"
                >
                  <template v-slot:item="data">
                    <template v-if="!isObject(data.item)">
                      <v-list-item-content v-text="data.item" />
                    </template>
                    <template v-else>
                      <v-list-item-avatar>
                        <v-icon
                          v-if="data.item.Type === 'Delivery'"
                          color="secondary"
                          large
                        >
                          mdi-truck
                        </v-icon>
                        <v-icon v-else color="secondary" large>
                          mdi-store
                        </v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title v-html="data.item.Name" />
                        <v-list-item-subtitle
                          v-text="getDeliveryPickupSubtitle(data.item)"
                        />
                      </v-list-item-content>
                    </template>
                  </template>
                </v-select>

                <!-- Delivery options section -->
                <v-expand-transition>
                  <section
                    v-if="isDelivery && deliveryFeeItems.length > 0"
                    class="mt-2 px-2"
                  >
                    <!-- Delivery fee option in case of only 1 delivery available -->
                    <div
                      v-if="deliveryFeeItems.length === 1"
                      class="text-subtitle-1 text--primary"
                    >
                      {{ deliveryFeeItems[0].Name }}
                    </div>

                    <!-- Delivery fee options in case of more than 1 delivery available -->
                    <v-radio-group
                      v-else
                      v-model="selectedDeliveryFeeItem"
                      hide-details="auto"
                      column
                      class="mt-0"
                      @change="onDeliverySelectionChange"
                    >
                      <v-radio
                        v-for="(
                          deliveryFeeItem, deliveryFeeItemIndex
                        ) in deliveryFeeItems"
                        :key="deliveryFeeItemIndex"
                        :value="deliveryFeeItem"
                      >
                        <template v-slot:label>
                          <div class="text-subtitle-1 text--primary">
                            {{ deliveryFeeItem.Name }}
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </section>
                </v-expand-transition>
              </v-col>
            </v-row>

            <v-expand-transition>
              <DeliveryAlert
                :alerts="deliveryAlerts"
                :improvements="improvements"
              />
            </v-expand-transition>

            <PickupAlert :visible="isPickup" :pickupFee="pickupFee" />
          </v-col>
        </v-row>
      </template>

      <!-- User Detail rows -->
      <v-row class="mb-6 mt-4">
        <v-chip
          class="my-1 mr-3"
          color="primary"
          v-if="productShoppingCart.length > 0"
        >
          <h3>2/3</h3>
        </v-chip>
        <h2 class="pl-2 pa-sm-0 primary--text">
          {{ $translate('ENTER_YOUR_PERSONAL_DETAILS') }}
        </h2>
      </v-row>
      <v-form v-model="valid" ref="form">
        <v-row class="mt-0">
          <v-col cols="12" md="6">
            <v-text-field
              :label="$translate('FIRSTNAME')"
              v-model="userObject.firstName"
              required
              outlined
              hide-details="auto"
              :rules="textRule"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              :label="$translate('LASTNAME')"
              v-model="userObject.lastName"
              required
              outlined
              hide-details="auto"
              :rules="textRule"
            />
          </v-col>

          <v-col cols="12" v-if="isDelivery">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  :loading="loadingAddress"
                  :disabled="loadingAddress"
                  :label="$translate('POSTAL_CODE')"
                  v-model="userObject.postalCode"
                  @change="getDeliveryDetails"
                  :rules="postalCodeRules"
                  outlined
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  :loading="loadingAddress"
                  :disabled="loadingAddress"
                  :label="$translate('HOUSE_NUMBER')"
                  v-model="userObject.houseNo"
                  required
                  outlined
                  hide-details="auto"
                  :rules="textRule"
                />
              </v-col>
              <v-col cols="12" v-if="noConfidenceInAddress === true">
                <v-alert text dense type="info">
                  {{ $translate('NO_CONFIDENCE_ADDRESS') }}
                </v-alert>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  :disabled="!editCityAndStreet"
                  :label="$translate('STREET')"
                  v-model="userObject.street"
                  required
                  outlined
                  hide-details="auto"
                  :rules="textRule"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  :disabled="!editCityAndStreet"
                  :label="$translate('CITY')"
                  v-model="userObject.city"
                  :rules="textRule"
                  outlined
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="userObject.countryCode"
                  :label="$translate('COUNTRY')"
                  :rules="textRule"
                  :items="countryCodes"
                  :disabled="countryCodes.length === 1"
                  @change="getDeliveryDetails"
                  hide-details="auto"
                  outlined
                >
                  <template slot="item" slot-scope="data">
                    {{ getCountryName(data.item, { select: 'official' }) }}
                  </template>
                  <template slot="selection" slot-scope="data">
                    {{ getCountryName(data.item, { select: 'official' }) }}
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" v-if="false" class="pt-0 mt-0">
                <v-row class="ma-0">
                  <h4 class="mb-1">{{ $translate('DELIVERY_ADDRESS') }}</h4>
                  <a
                    v-if="!editCityAndStreet"
                    class="pl-2"
                    @click="editCityAndStreetData"
                  >
                    {{ $translate('EDIT') }}
                  </a>
                </v-row>
                <div>{{ userObject.street }} {{ userObject.houseNo }}</div>
                <div>{{ userObject.postalCode }} {{ userObject.city }}</div>
                <div>{{ userObject.countryCode }}</div>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              :label="$translate('EMAIL')"
              v-model="userObject.email"
              disabled
              outlined
              hide-details="auto"
              :rules="emailRules"
            />
          </v-col>
          <v-col
            v-if="
              $store.getters['auth/userName'] === '' && userObject.email === ''
            "
            cols="12"
            md="6"
          >
            <v-text-field
              :label="$translate('CONFIRM_EMAIL')"
              v-model="confirmEmail"
              outlined
              hide-details="auto"
              :rules="confirmEmailRules"
            />
          </v-col>
          <v-col cols="6" md="3">
            <v-select
              v-model="selectedCountryCode"
              :items="countryCallingCodes"
              :label="$translate('COUNTRY_CODE')"
              item-text="name"
              item-value="callingCode"
              outlined
            ></v-select>
          </v-col>
          <v-col cols="6" md="3">
            <v-text-field
              :label="$translate('PHONE_NR')"
              v-model="nationalPhoneNo"
              :rules="phoneNumberRules"
              hide-details="auto"
              required
              outlined
            />
            <v-alert
              v-if="phoneError"
              type="warning"
              outlined
              text
              dense
              class="mt-2 mb-1"
            >
              {{ $translate('PHONE_NO_RECOGNITION_DESC') }}
            </v-alert>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="remarks"
              :label="$translate('REMARK_OPTIONAL')"
              rows="2"
              outlined
              hide-details="auto"
            />
          </v-col>
        </v-row>
      </v-form>

      <!-- Date & time selection -->
      <template v-if="productShoppingCart.length > 0">
        <v-row class="mb-6 mt-7">
          <v-chip
            class="my-1 mr-3"
            text-color="white"
            :color="disableDateSection ? 'rgba(0, 0, 0, 0.20)' : 'primary'"
          >
            <h3>3/3</h3>
          </v-chip>
          <h2
            :class="`pl-2 pa-sm-0
              ${disableDateSection ? 'disabled-text' : 'primary--text'}`"
          >
            {{ $translate('PICK_YOUR_DATE_AND_TIME') }}
          </h2>
        </v-row>
        <v-row class="my-0 mt-3">
          <!-- Reminder for international delivery -->
          <v-col
            v-if="deliveryNote !== '' && orderType.Type === 'Delivery'"
            cols="12"
          >
            <v-alert type="warning" icon="mdi-alert-circle" class="mb-0">
              {{ deliveryNote }}
            </v-alert>
          </v-col>

          <!-- Start date picker -->
          <v-col cols="12" md="6">
            <h3 :class="disableDateSection ? 'disabled-text' : 'primary--text'">
              {{ $translate('DATE') }}
            </h3>
            <v-menu
              ref="menuStartDate"
              v-model="menuStartDate"
              :close-on-content-click="false"
              :return-value.sync="orderDetails.StartDate"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="computedDateFormattedStartDate"
                  v-bind="attrs"
                  v-on="on"
                  readonly
                  outlined
                  ref="menuStartDate"
                  append-icon="today"
                  hide-details="auto"
                  :disabled="disableDateSection"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                no-title
                @change="getStartBatches"
                event-color="primary"
                :allowed-dates="allowedStartDates"
                :events="allowedStartDates"
                @input="$refs.menuStartDate.save(orderDetails.StartDate)"
                scrollable
              />
            </v-menu>
          </v-col>

          <!-- Start time picker -->
          <v-col cols="12" md="6">
            <h3 :class="disableDateSection ? 'disabled-text' : 'primary--text'">
              {{ $translate('TIME') }}
            </h3>
            <v-select
              v-model="orderDetails.StartTime"
              :items="listOfStartTimes"
              hide-details="auto"
              outlined
              @change="getEndDates"
              :disabled="disableDateSection"
            >
              <template slot="selection" slot-scope="data">
                {{ data.item.StartTime }}
                <!-- - {{ data.item.EndTime }} -->
              </template>
              <template slot="item" slot-scope="data">
                <template>
                  <v-row justify="space-between" class="py-1">
                    {{ data.item.StartTime + ' - ' + data.item.EndTime }}
                    <div class="text-xs-center">
                      <v-chip
                        :dark="getChipData('darkModus', data.item)"
                        :style="`background-color: ${getChipData(
                          'color',
                          data.item
                        )}`"
                      >
                        {{ $translate(getChipData('text', data.item)) }}
                      </v-chip>
                    </div>
                  </v-row>
                </template>
              </template>
            </v-select>
          </v-col>

          <!-- End date picker -->
          <v-col v-if="possibleEndDateList.length > 0" cols="12" md="6">
            <v-menu
              ref="menuEndDate"
              v-model="menuEndDate"
              :close-on-content-click="false"
              :return-value.sync="orderDetails.EndDate"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="computedDateFormattedEndDate"
                  :label="$translate('PLEASE_ENDDATE')"
                  v-bind="attrs"
                  v-on="on"
                  readonly
                  outlined
                  :disabled="possibleEndDateList.length < 2"
                  append-icon="today"
                  hide-details="auto"
                />
              </template>
              <v-date-picker
                v-model="endDate"
                no-title
                event-color="primary"
                :allowed-dates="allowedEndDate"
                :events="allowedEndDate"
                @input="$refs.menuEndDate.save(orderDetails.EndDate)"
                scrollable
              />
            </v-menu>
          </v-col>

          <!-- End time picker -->
          <v-col v-if="listOfEndTimes.length > 0" cols="12" md="6">
            <v-select
              v-model="orderDetails.EndTime"
              :label="$translate('PLEASE_ENDTIME')"
              :items="listOfEndTimes"
              :disabled="listOfEndTimes.length < 2"
              hide-details="auto"
              outlined
            >
              <template slot="selection" slot-scope="data">
                {{ data.item.StartTime }} - {{ data.item.EndTime }}
              </template>
              <template slot="item" slot-scope="data">
                <template>
                  <v-row justify="space-between" class="py-1">
                    {{ data.item.StartTime + ' - ' + data.item.EndTime }}
                    <div class="text-xs-center">
                      <v-chip
                        :dark="getChipData('darkModus', data.item)"
                        :style="`background-color: ${getChipData(
                          'color',
                          data.item
                        )}`"
                      >
                        {{ $translate(getChipData('text', data.item)) }}
                      </v-chip>
                    </div>
                  </v-row>
                </template>
              </template>
            </v-select>
          </v-col>
        </v-row>
      </template>

      <!-- Tickets for selected delivery / pickup event -->
      <v-expand-transition>
        <section v-if="Object.keys(listOfTickets).length > 0">
          <h3 class="mt-4 mb-2">
            {{ $translate('CHOOSE_AN_OPTION') }}
          </h3>

          <v-row class="mb-3">
            <v-col
              v-for="(item, index) in listOfTickets"
              cols="12"
              md="8"
              :key="index"
            >
              <v-card elevation="2">
                <v-row class="ma-0">
                  <v-col cols="9">
                    <h4>{{ item.Name }}</h4>
                    <div v-if="item.Price != null" class="text-subtitle-1">
                      {{ $translate('UNIT_PRICE') }}:
                      {{ formatter.format(getTicketUnitPrice(item)) }}
                      <span class="text-caption text--secondary">{{
                        getPriceSuffix()
                      }}</span>
                    </div>
                    <div v-if="isProfUser">
                      <span class="text-body-2">
                        {{ formatter.format(item.Price) }}
                      </span>
                      <span class="text-caption text--secondary">
                        {{ getVATText(item.VatPercentage) }}
                      </span>
                    </div>
                    <div
                      class="hidden-sm-and-down text-body-2 text--secondary mt-2"
                    >
                      {{ item.Description }}
                    </div>
                  </v-col>

                  <v-col cols="3" align="end">
                    <v-row justify="start" justify-md="end">
                      <v-btn-toggle group>
                        <v-btn
                          large
                          @click="
                            item.Amount === item.MinAmount
                              ? (item.Amount = item.MinAmount)
                              : item.Amount--
                          "
                          icon
                          color="primary"
                        >
                          <v-icon>remove_circle</v-icon>
                        </v-btn>
                        <h3 class="ticketAmount">{{ item.Amount }}</h3>
                        <v-btn
                          large
                          @click="
                            item.Amount === item.MaxAmount
                              ? (item.Amount = item.MaxAmount)
                              : item.Amount++
                          "
                          icon
                          color="primary"
                        >
                          <v-icon>add_circle</v-icon>
                        </v-btn>
                      </v-btn-toggle>
                    </v-row>
                  </v-col>
                  <v-col class="hidden-md-and-up" cols="12">
                    <p class="ma-0">{{ item.Description }}</p>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </section>
      </v-expand-transition>
    </v-col>

    <!-- coupon recipient details: hidden until backend implementation completes -->
    <v-card
      v-if="virtualProductShoppingCart.length"
      flat
      class="pa-6 pt-2 mt-6"
    >
      <v-card-title class="px-2" color="grey lighten-2">
        <h3>{{ $translate('DETAILS_FROM_RECIPIENT') }}</h3>
      </v-card-title>
      <v-card-text class="pt-3 px-0" color="grey lighten-4">
        <!-- form for the receiver -->
        <v-form v-model="validationOfRecipientForm" ref="formRecipient">
          <!-- description text for coupon receiver -->
          <div
            class="text--secondary mb-4"
            v-html="$translate('RECIPIENT_DETAILS_DESCRIPTION')"
          ></div>

          <!-- firstname, lastname & email -->
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                :label="$translate('RECIPIENT_FIRSTNAME')"
                v-model="recipientUserObject.FirstName"
                required
                :rules="textRule"
                hide-details="auto"
                background-color="white"
                filled
                @input="resetCouponForm"
                @change="resetCouponForm"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :label="$translate('RECIPIENT_LASTNAME')"
                v-model="recipientUserObject.LastName"
                required
                :rules="textRule"
                hide-details="auto"
                background-color="white"
                filled
                @input="resetCouponForm"
                @change="resetCouponForm"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :label="$translate('RECIPIENT_EMAIL')"
                v-model="recipientUserObject.Email"
                :rules="emailRules"
                hide-details="auto"
                background-color="white"
                filled
                @change="resetCouponForm"
              />
            </v-col>
            <!-- receiver message -->
            <v-col cols="12">
              <v-textarea
                :label="$translate('RECIPIENT_MESSAGE')"
                v-model="recipientUserObject.MessageText"
                rows="2"
                background-color="white"
                filled
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Empty order error -->
    <v-slide-y-transition>
      <v-row class="mt-3 ml-3" v-if="emptyOrderError">
        <span class="error-message">
          {{ $translate('EVENT_MESSAGE_SELECT_PRODUCT') }}
        </span>
      </v-row>
    </v-slide-y-transition>

    <template v-for="(growthVitamin, gvIndex) in growthVitamins">
      <v-row :key="gvIndex" class="mb-1 mt-3">
        <v-col cols="12" md="6" offset-md="6">
          <v-card
            v-if="growthVitamin.DiscountInfo.Type == 'Free Delivery'"
            class="primary-font-color"
            color="primary"
            flat
          >
            <v-row align="center">
              <v-avatar size="32" tile class="mx-3">
                <v-icon color="white">mdi-truck</v-icon>
              </v-avatar>
              <v-col class="px-3 py-1">
                <div class="text-subtitle-2">
                  {{ $translate('FREE_DELIVERY') }}
                </div>
                <div class="text-caption" v-html="growthVitamin.Description" />
              </v-col>
            </v-row>
          </v-card>
          <v-card v-else class="primary-font-color" color="primary" flat>
            <v-row align="center">
              <v-avatar size="32" tile class="mx-3">
                <v-icon color="white">mdi-lightbulb-on</v-icon>
              </v-avatar>
              <v-col class="px-3 py-1">
                <div class="text-subtitle-2">{{ growthVitamin.Name }}</div>
                <div class="text-caption" v-html="growthVitamin.Description" />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-row class="mt-1">
      <v-col cols="12" class="py-1">
        <DeliveryAlert :alerts="deliveryAlerts" :improvements="improvements" />
      </v-col>
      <v-col cols="12" class="py-1">
        <PickupAlert :visible="isPickup" :pickupFee="pickupFee" />
      </v-col>

      <!-- extra options and tickets alert with the cost of it -->
      <v-col v-if="getOptionText" cols="12" md="6" offset-md="6" class="py-1">
        <v-card elevation="0">
          <v-alert type="warning" icon="mdi-alert-circle" class="mb-0">
            {{ getOptionText }}
          </v-alert>
        </v-card>
      </v-col>
    </v-row>

    <!-- Buttons -->
    <v-row class="mt-7">
      <v-btn
        color="secondary"
        dark
        class="white--text text-capitalize"
        @click="eventDetailBack()"
      >
        {{ $translate('BACK') }}
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        dark
        class="primary-font-color text-capitalize"
        @click="openOrderOverview()"
        :disabled="continueBtnDisabled"
        :loading="paymentBtnLoading || loadingAddress"
      >
        {{ $translate('CONTINUE') }}
      </v-btn>
    </v-row>
    <BookingPhoneDialog
      v-model="userObject.phoneNo"
      :phoneNumberDialog="phoneNumberDialog"
      @continue="ifCorrectPhoneNumber"
      @close="closePhonenumberDialog"
    />
  </v-col>
</template>

<script>
import ErrorCard from '@/components/generalComponents/ErrorHandling/ErrorCard';
import { mapGetters } from 'vuex';
import doRequest from '@/api/dssRequest';
import forwardGeocoding from '@/api/Geocoding';
import utility from '@/shared/utility';
import { requiredRules, emailRules } from '@/shared/validations_rules';
import { getVATText } from '@/objects/Product';
import PickupFeeJS from '@/objects/PickupFee';
import DeliveryAlert from '@/components/generalComponents/deliveryPickUp/DeliveryAlert';
import PickupAlert from '@/components/generalComponents/deliveryPickUp/PickupAlert';
import BookingPhoneDialog from './Booking_Phone_Dialog.vue';
import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { countries } from 'countries-list';

let countriesCode = require('i18n-iso-countries');
countriesCode.registerLocale(require('i18n-iso-countries/langs/en.json'));
countriesCode.registerLocale(require('i18n-iso-countries/langs/nl.json'));
countriesCode.registerLocale(require('i18n-iso-countries/langs/de.json'));

export default {
  name: 'BookingDetails_Products',
  components: {
    ErrorCard,
    BookingPhoneDialog,
    DeliveryAlert,
    PickupAlert,
  },
  props: {
    userEmail: String,
  },
  data() {
    return {
      // ErrorCard variables
      error: false,
      errorMessage: "Couldn't get available dates...",
      errorToRecallFunction: 'getStartDates',

      productShoppingCart: [],
      orderDetails: {},
      orderType: {},
      allLocations: [],
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      listOfStartTimes: [],
      listOfEndTimes: [],
      possibleStartDateList: [],
      possibleEndDateList: [],
      countryCodes: [],
      menuStartDate: '',
      menuEndDate: '',
      startDate: '',
      endDate: '',
      minOrderValue: '',

      deliveryAndPickUpDataLoaded: false,
      deliveryAndPickUpData: {},
      PickUpEvents: {},
      listOfTickets: {},
      products: {},
      remarks: '',

      succesfulStartDate: false,

      emptyOrderError: false,

      // coupon recipient
      validationOfRecipientForm: null,
      recipientUserObject: {
        MessageText: '',
      },
      virtualProductShoppingCart: [],

      // User details
      hidePassword: true,
      valid: null,
      userObject: this.$store.getters['auth/user'] || {},
      confirmEmail: '',
      password: '',
      passwordRepeat: '',
      // rules
      textRule: [...requiredRules],
      postalCodeRules: [
        ...requiredRules,
        (v) =>
          (v != null &&
            ((/^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i.test(v.trim()) &&
              this.userObject.countryCode === 'NL') ||
              this.userObject.countryCode !== 'NL')) ||
          this.$translate('INVALID_POSTAL_CODE'),
      ],
      emailRules: [...requiredRules, ...emailRules],
      confirmEmailRules: [
        (v) =>
          (!!v && v) === this.userObject.email ||
          this.$translate('EMAIL_NO_MATCH'),
        ...emailRules,
      ],
      phoneError: false,
      phoneRules: [...requiredRules],
      deliveryText: '',
      deliveryError: null,
      deliveryAlerts: [],
      showCityAndStreet: false,
      editCityAndStreet: false,
      loadingAddress: false,
      noConfidenceInAddress: false,

      // Indicate the state of geocoding
      isGeocoding: false,

      paymentBtnLoading: false,

      phoneNumberDialog: false,
      phonenumber: '',
      userHasAccount: false,
      logIn: false,
      addedLocale: false,
      deliveryNote: '',
      improvements: [],
      noDelivery: false,

      deliveryFeeItems: [],
      selectedDeliveryFeeItem: {},

      pickupFee: null,

      countryCallingCodes: [],
      selectedCountryCode: '',
      nationalPhoneNo: '',
      phoneNumberRules: [
        (value) =>
          !value ||
          !!this.validateNumber(value) ||
          this.$translate('INVALID_PHONE_NO'),
      ],
    };
  },
  created() {
    this.getCountryCallingCode();
    this.formatPhoneNumber();
  },
  watch: {
    selectedCountryCode() {
      this.validateNumber();
    },
  },
  computed: {
    ...mapGetters({
      GVTimer: 'getGVTimer',
      isProfUser: 'isProfUser',
    }),
    deliveryPickUpSelect() {
      const selectionItems = this.PickUpEvents;
      const hasPickUp =
        this.PickUpEvents != null && this.PickUpEvents.length > 0;
      // add header
      if (hasPickUp) {
        selectionItems.unshift({ header: 'pickup' });
      }

      // if delivery is possible then add option
      if (!this.noDelivery) {
        if (hasPickUp) {
          selectionItems.unshift({ divider: true });
        }
        selectionItems.unshift({
          Name: this.$translate('DELIVERY'),
          Type: 'Delivery',
        });
      }

      return selectionItems;
    },
    disableDateSection() {
      if (this.orderType.Type !== 'Delivery' && this.orderType.Type != null)
        return false;
      if (this.userObject.postalCode && this.orderType.Type === 'Delivery')
        return false;
      return true;
    },
    checkIfUserHasAccountData() {
      if (this.userIsLoggedIn) {
        return !!(
          this.$store.getters['auth/user'].city !== '' &&
          this.$store.getters['auth/user'].street !== '' &&
          this.$store.getters['auth/user'].postalCode !== '' &&
          this.$store.getters['auth/user'].houseNo !== ''
        );
      }
      return false;
    },
    computedDateFormattedStartDate() {
      return this.formatDate(this.startDate);
    },
    computedDateFormattedEndDate() {
      return this.formatDate(this.endDate);
    },
    userObjectId() {
      return this.userIsLoggedIn
        ? this.$store.getters['auth/userName']
        : this.$store.getters['auth/tmpUserId'];
    },
    couponFieldsNotEmpty() {
      // check if there is something is filled in
      return (
        (this.recipientUserObject.FirstName != null &&
          this.recipientUserObject.FirstName !== '') ||
        (this.recipientUserObject.LastName != null &&
          this.recipientUserObject.LastName !== '') ||
        (this.recipientUserObject.Email != null &&
          this.recipientUserObject.Email !== '')
      );
    },
    checkForOnlyCoupon() {
      // check if you have to show the phone number or not
      const noCouponItems = this.$store.getters['shoppingCart/orders'].filter(
        (item) => item.ShoppingCartType !== 'VirtualProduct'
      );
      // return the boolean
      return noCouponItems.length;
    },
    userIsLoggedIn() {
      return this.$store.getters['auth/userName'];
    },
    getOptionText() {
      let totalPrice = 0;

      Object.values(this.listOfTickets).forEach((ticket) => {
        let ticketPrice = 0;
        if (this.isProfUser) {
          ticketPrice = ticket.Price - ticket.VatPrice;
        } else {
          ticketPrice = ticket.Price;
        }
        totalPrice += ticketPrice * ticket.Amount;
      });

      if (totalPrice === 0) {
        return '';
      }

      const textTemp = `${this.$translate(
        'OPTIONS_EXTRA_FEE'
      )}: ${this.formatter.format(totalPrice)}`;

      if (this.isProfUser) {
        return `${textTemp} (${this.$translate('EXCL_VAT')})`;
      }

      return textTemp;
    },
    continueBtnDisabled() {
      if (
        this.orderType.Type === undefined &&
        this.productShoppingCart.length !== 0
      )
        return true;
      if (this.error && this.orderType.Type === 'Delivery') return true;
      if (this.isDelivery && this.deliveryError) return true;
      if (this.isPickup && !this.isPickupPossible) return true;

      return false;
    },
    isDelivery() {
      if (
        this.orderType.Type === 'Delivery' ||
        this.orderType.Type === 'Shiplee'
      )
        return true;

      return false;
    },
    isPickup() {
      return this.orderType != null && this.orderType.Type === 'PickUp';
    },
    isPickupPossible() {
      return this.pickupFee != null && this.pickupFee.isPossible();
    },
    growthVitamins() {
      let gvList = [];
      const allGrowthVitamins = this.$store.getters['getGrowthVitamins'];
      if (allGrowthVitamins == null) {
        return gvList;
      }
      allGrowthVitamins.forEach((gv) => {
        if (gv.DiscountInfo != null && gv.DiscountInfo.Type !== 'Tip') {
          gvList.push(gv);
        }
      });
      return gvList;
    },
  },
  mounted() {
    if (this.$store.getters['auth/userName'] === '') {
      this.userObject.email = this.userEmail;
    }

    // filter array items based on ShoppingCartType === 'Product' and put it in an array
    this.productShoppingCart = this.$store.getters[
      'shoppingCart/orders'
    ].filter((item) => item.ShoppingCartType === 'Product');

    // filter array items based on ShoppingCartType === 'VirtualProduct' and put it in an array
    this.virtualProductShoppingCart = this.$store.getters[
      'shoppingCart/orders'
    ].filter((item) => item.ShoppingCartType === 'VirtualProduct');

    if (this.productShoppingCart.length !== 0) {
      this.getDeliveryAndPickUpEvents();
    }
    this.getAwardedGrowthVitaminCards();
  },
  methods: {
    getCountryName(code, selection) {
      let locale = '';
      switch (Number(this.$store.getters['auth/userLanguage'].LanguageId)) {
        case 13:
          locale = 'en';
          break;
        case 14:
          locale = 'nl';
          break;
        case 17:
          locale = 'de';
          break;
        default:
          locale = 'en';
      }
      return countriesCode.getName(code, locale, selection);
    },
    getCountryCallingCode() {
      const countryCallingData = Object.values(countries);
      this.countryCallingCodes = countryCallingData.map((country) => ({
        name: country.name + ' ' + `(+${country.phone})`,
        code: country.phone,
        callingCode: `+${country.phone}`,
      }));
    },
    closePhonenumberDialog() {
      this.phoneNumberDialog = false;
    },
    checkPhoneNumber() {
      this.phonenumber = this.userObject.phoneNo;
      this.phoneError =
        !/^((\+|00(\s|\s?\-\s?)?)31(\s|\s?\-\s?)?(\(0\)[\-\s]?)?|0)[1-9]((\s|\s?\-\s?)?[0-9])((\s|\s?-\s?)?[0-9])((\s|\s?-\s?)?[0-9])\s?[0-9]\s?[0-9]\s?[0-9]\s?[0-9]\s?[0-9]$/.test(
          this.userObject.phoneNo
        ) && this.userObject.phoneNo !== '';
    },
    formatPhoneNumber() {
      if (this.userObject.phoneNo) {
        //get only National number to display. country code will be shown in the selection
        const phoneNumber = parsePhoneNumberFromString(this.userObject.phoneNo);
        if (phoneNumber) {
          const nationalNumber = phoneNumber.nationalNumber;
          const countryCallingCode = phoneNumber.countryCallingCode;
          this.nationalPhoneNo = nationalNumber;
          this.selectedCountryCode = `+${countryCallingCode}`;
          console.log('nationalNumber', nationalNumber);
          console.log('selectedCountryCode', countryCallingCode);
        }
      } else {
        this.selectedCountryCode = '+31';
      }
    },
    validateNumber() {
      if (this.selectedCountryCode && this.nationalPhoneNo) {
        let phoneNumber = this.selectedCountryCode + this.nationalPhoneNo;
        const isValid = isValidPhoneNumber(phoneNumber);
        console.log('isValid ?', isValid, phoneNumber);
        return isValid;
      }
    },
    openOrderOverview() {
      if (!this.$refs.form.validate()) {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        return;
      }

      if (this.pickupAlert) {
        return;
      }

      this.orderDetails.StartDate = this.startDate;
      this.orderDetails.EndData = this.endDate;

      if (this.nationalPhoneNo !== '') {
        this.userObject.phoneNo =
          this.selectedCountryCode + this.nationalPhoneNo;
      } else this.userObject.phoneNo = '';

      // Because the user could enter a different address than his home address, don't send the latitude/longitude of the user.
      // The latitude/longitude of the user come from the getUserInfo from the BE and belong to the home address.
      this.userObject.latitude = -200;
      this.userObject.longitude = -200;
      this.$store.commit('setUserDetails', this.userObject);
      this.$store.commit('setOrderDetails', this.orderDetails);
      this.$store.commit('setOrderType', this.orderType);
      this.$store.commit('setListOfTickets', this.listOfTickets);
      this.$store.commit('setRecipientUserObject', this.recipientUserObject);
      this.$store.commit('setNotes', this.remarks);
      // if form is not valid stop the function
      /*
        check if there are virtualProducts and there is one field filled in
        This is used to don't book the products before your details are all correct
       */
      if (this.virtualProductShoppingCart.length && this.couponFieldsNotEmpty) {
        // check if the form is correct
        if (!this.$refs.formRecipient.validate()) {
          window.EventBus.fire('activateSnackBar', {
            text: this.$translate('LINDA_FILL_REQUIRED'),
            color: 'warning',
          });
          return;
        }
      }

      if (
        this.checkForOnlyCoupon &&
        this.userObject.phoneNo !== '' &&
        !/^((\+|00(\s|\s?\-\s?)?)31(\s|\s?\-\s?)?(\(0\)[\-\s]?)?|0)[1-9]((\s|\s?\-\s?)?[0-9])((\s|\s?-\s?)?[0-9])((\s|\s?-\s?)?[0-9])\s?[0-9]\s?[0-9]\s?[0-9]\s?[0-9]\s?[0-9]$/.test(
          this.userObject.phoneNo
        )
      ) {
        this.phoneNumberDialog = true;
      } else {
        this.ifCorrectPhoneNumber();
      }
    },
    ifCorrectPhoneNumber() {
      this.$emit('openOrderOverview');
    },
    getAwardedGrowthVitaminCards() {
      // variable to add orders to payload
      const allEventsForOrder = {};
      // loop through ticket/rental orders and set them in the variable
      this.$store.getters['shoppingCart/orders'].forEach((orderItem) => {
        // to create index in array with that EventId
        if (!allEventsForOrder.hasOwnProperty(orderItem.EventId)) {
          allEventsForOrder[orderItem.EventId] = [];
        }
        // add the items to that index
        allEventsForOrder[orderItem.EventId].push(orderItem.OrderId);
      });

      const payload = {
        Reservations: allEventsForOrder || {},
      };

      doRequest(
        (response) => {
          this.$store.commit(
            'setGrowthVitamins',
            response.data.GrowthVitaminCards
              ? response.data.GrowthVitaminCards
              : {}
          );
        },
        () => {},
        'getAwardedGrowthVitaminCards',
        this.userObjectId,
        payload
      );
    },
    updateDeliveryData() {
      if (this.orderType === null || Object.keys(this.orderType).length === 0)
        return;

      if (this.isDelivery) {
        // call function for new deliveryFee
        this.editCityAndStreet = true;
        this.noConfidenceInAddress = false;
        this.getDeliveryDetails();
      } else {
        // remove deliveryText to remove the alert
        this.deliveryText = '';
        this.deliveryAlerts = [];

        this.getStartDates();
      }

      this.getPickupFee();
    },
    getDeliveryAndPickUpEvents() {
      // Retrieving the delivery and pick up Events
      const payload = {
        Events: [],
        Reservations: this.getShoppingCartReservations(),
      };

      this.$store.getters['shoppingCart/orders'].forEach((productInCart) => {
        if (productInCart.PriceCategories.length <= 0) {
          payload.Events.push(productInCart.EventId);
        }
      });

      doRequest(
        (response) => {
          this.PickUpEvents = response.data.PickUpEvents;
          this.countryCodes = response.data.DeliveryCountryCodes;

          if (response.data.DeliveryEvents.length === 0) {
            this.noDelivery = true;
          }

          this.orderType = {};
          this.updateDeliveryData();

          if (this.countryCodes.includes('NL')) {
            this.userObject.countryCode = 'NL';
          }

          if (
            response.data.PickUpEvents.length === 0 &&
            response.data.DeliveryEvents.length === 0
          ) {
            // Neither DeliveryEvents nor PickUpEvents are available, cannot continue
            this.error = true;
            this.errorMessage = this.$translate('NO_DELIVERY_OR_PICKUP');
            this.errorToRecallFunction = 'getDeliveryAndPickUpEvents';
            return;
          }
          // to show the colums after the data is received
          this.deliveryAndPickUpDataLoaded = true;
        },
        () => {
          this.error = true;
          this.errorMessage = this.$translate('NO_DELIVERY_OR_PICKUP');
          this.errorToRecallFunction = 'getDeliveryAndPickUpEvents';
        },
        'getDeliveryAndPickUpEvents',
        this.userObjectId,
        payload
      );
    },
    getDeliveryDetails() {
      this.deliveryFeeItems = [];
      this.selectedDeliveryFeeItem = {};
      this.clearDeliveryFeeDetails();
      if (!this.userObject.postalCode) {
        return;
      }

      const todayDate = utility.getCurrentDateDMY();

      const payload = {
        UserInfo: this.getUserInfo(),
        StartDate: todayDate,
        PostalCode: this.userObject.postalCode,
        Country: this.userObject.countryCode,
        Reservations: this.getShoppingCartReservations(),
        OneClickBooking: true,
      };

      doRequest(
        (response) => {
          if (response.data.DeliveryFees != null) {
            this.deliveryFeeItems = [...response.data.DeliveryFees];
          }
          if (this.deliveryFeeItems.length > 0) {
            this.selectedDeliveryFeeItem = { ...this.deliveryFeeItems[0] };
          }
          this.onDeliverySelectionChange();
        },
        () => {
          this.deliveryText = this.$translate('NO_DELIVERY_FOR_POSTAL_CODE');
          this.deliveryError = true;
          this.deliveryAlerts = [
            {
              type: 'error',
              text: this.deliveryText,
            },
          ];
        },
        'getDeliveryDetailsV2',
        this.$store.getters['auth/userObjectId'],
        payload
      );
    },
    getDeliveryFeeText(deliveryDetails) {
      let deliveryPrice = 0;
      let priceSuffix = '';
      if (this.isProfUser) {
        deliveryPrice =
          deliveryDetails.ResultSet.Price - deliveryDetails.ResultSet.VatPrice;
        priceSuffix = ` (${this.$translate('EXCL_VAT')})`;
      } else {
        deliveryPrice = deliveryDetails.ResultSet.Price;
      }

      return `${this.$translate('DELIVERY_FEE')}: ${this.formatter.format(
        deliveryPrice
      )}${priceSuffix}`;
    },
    getStartBatches() {
      this.error = false;
      const payload = {
        StartDate: this.computedDateFormattedStartDate,
        Products: {},
        OneClickBooking: true,
        Reservations: {},
      };

      const cartevent = this.$store.getters['shoppingCart/orders'];
      // payload.Reservations[cartevent[0].EventId] = [cartevent[0].OrderId];

      if (cartevent != null) {
        cartevent.forEach((reservation) => {
          if (payload.Reservations[reservation.EventId] == null) {
            payload.Reservations[reservation.EventId] = [];
          }
          payload.Reservations[reservation.EventId].push(reservation.OrderId);
        });
      }

      if (this.orderDetails.Location != null) {
        payload.LocationId = this.orderDetails.Location.LocationId;
      }

      doRequest(
        (response) => {
          this.listOfStartTimes = [];
          this.possibleEndDateList = [];
          this.listOfEndTimes = [];

          if (response.data.StartTimes != null) {
            this.listOfStartTimes = response.data.StartTimes;
            this.orderDetails.StartTime = response.data.DefaultStartTime;
          }

          if (response.data.EndTimes != null) {
            this.listOfEndTimes = response.data.EndTimes;
            this.orderDetails.EndTime = response.data.DefaultEndTime;
          }

          if (response.data.EndDates != null) {
            response.data.EndDates.forEach((date) => {
              this.possibleEndDateList.push(this.formatDate(date));
            });
            this.orderDetails.EndDate = this.formatDate(
              response.data.DefaultEndDate
            );
            this.endDate = this.orderDetails.EndDate;
          }

          if (response.data.Tickets != null) {
            this.listOfTickets = response.data.Tickets;
          }
        },
        () => {
          this.error = true;
          this.errorMessage = "Couldn't get start times for this event...";
          this.errorToRecallFunction = 'getStartBatches';
        },
        'getStartBatches',
        this.orderType.EventId,
        payload
      );
    },
    getStartDates() {
      this.error = false;
      this.possibleStartDateList = [];

      const todayDate = utility.getCurrentDateDMY();

      const payload = {
        StartDate: todayDate,
        LocationId: '',
        Products: {},
        OneClickBooking: true,
        Reservations: {},
      };

      const cartevent = this.$store.getters['shoppingCart/orders'];
      // payload.Reservations[cartevent[0].EventId] = [cartevent[0].OrderId];

      if (cartevent != null) {
        cartevent.forEach((reservation) => {
          if (payload.Reservations[reservation.EventId] == null) {
            payload.Reservations[reservation.EventId] = [];
          }
          payload.Reservations[reservation.EventId].push(reservation.OrderId);
        });
      }

      doRequest(
        (response) => {
          this.getStartDatesSuccessfull(response.data);
        },
        () => {
          this.error = true;
          this.errorMessage = "Couldn't get start dates for this event...";
          this.errorToRecallFunction = 'getStartDates';
        },
        'getStartDates',
        this.orderType.EventId,
        payload
      );
    },
    getStartDatesSuccessfull(dateObject) {
      dateObject.StartDates.forEach((date) => {
        this.possibleStartDateList.push(this.formatDate(date));
      });
      if (dateObject.EndDates != null) {
        dateObject.EndDates.forEach((date) => {
          this.possibleEndDateList.push(this.formatDate(date));
        });
      }
      if (dateObject.StartTimes != null) {
        this.listOfStartTimes = dateObject.StartTimes;
      }

      if (dateObject.EndTimes != null) {
        this.listOfEndTimes = dateObject.EndTimes;
      }

      if (dateObject.Tickets != null) {
        this.listOfTickets = dateObject.Tickets;
      }

      this.orderDetails.StartDate = this.formatDate(
        dateObject.DefaultStartDate
      );

      if (dateObject.DefaultStartDate === undefined) {
        this.listOfStartTimes = [];
        this.succesfulStartDate = true;
      } else {
        this.succesfulStartDate = false;
      }

      this.startDate = this.orderDetails.StartDate;

      this.orderDetails.EndDate = this.formatDate(dateObject.DefaultEndDate);
      this.endDate = this.orderDetails.EndDate;

      this.orderDetails.StartTime = dateObject.DefaultStartTime;
      this.orderDetails.EndTime = dateObject.DefaultEndTime;
    },
    getEndDates() {
      this.error = false;
      this.possibleEndDateList = [];

      const payload = {
        PartnerEventId: this.orderType.EventId,
        orderType: this.orderType,
        StartDate: this.computedDateFormattedStartDate,
        StartTime: this.orderDetails.StartTime.StartTime,
        LocationId:
          this.eventDetails.Type === 'Webshop'
            ? ''
            : this.orderDetails.Location.LocationId,
        Products: {},
        OneClickBooking: true,
        Reservations: {},
      };

      const cartevent = this.$store.getters['shoppingCart/orders'];
      // payload.Reservations[cartevent[0].EventId] = [cartevent[0].OrderId];

      if (cartevent != null) {
        cartevent.forEach((reservation) => {
          if (payload.Reservations[reservation.EventId] == null) {
            payload.Reservations[reservation.EventId] = [];
          }
          payload.Reservations[reservation.EventId].push(reservation.OrderId);
        });
      }

      doRequest(
        (response) => {
          this.possibleEndDateList = [];

          if (response.data.EndDates != null) {
            response.data.EndDates.forEach((date) => {
              this.possibleEndDateList.push(this.formatDate(date));
            });
            this.listOfEndTimes = response.data.EndTimes;
            this.listOfTickets = response.data.Tickets;
            this.orderDetails.EndDate = this.formatDate(
              response.data.DefaultEndDate
            );
            this.endDate = this.orderDetails.EndDate;
            this.orderDetails.EndTime = response.data.DefaultEndTime;
          }
        },
        () => {
          this.error = true;
          this.errorMessage = "Couldn't get end dates for this event...";
          this.errorToRecallFunction = 'getEndDates';
        },
        'getEndDates',
        this.eventDetails.EventId,
        payload
      );
    },
    getLocations() {
      this.error = false;
      const payload = {
        Densities: [0, 1, 2, 3, 4],
      };
      doRequest(
        (response) => {
          this.allLocations = response.data.Locations;
          [this.orderDetails.Location] = this.allLocations;
          this.getStartDates();
        },
        () => {
          this.error = true;
          this.errorMessage = "Couldn't find locations for this event...";
          this.errorToRecallFunction = 'getLocations';
        },
        'getLocations',
        this.eventDetails.EventId,
        payload
      );
    },
    allowedStartDates(val) {
      return this.possibleStartDateList.indexOf(val) !== -1;
    },
    allowedEndDate(val) {
      return this.possibleEndDateList.indexOf(val) !== -1;
    },
    getChipData(sendedInfo, item) {
      switch (sendedInfo) {
        case 'text':
          if (item.Occupancy < 0.25) return 'NOT_BUSY'; // green
          if (item.Occupancy < 0.5) return 'NORMAL'; // yellow
          return 'ALMOST_FULL'; // red
        case 'color':
          if (item.State === 0) return 'rgba(25, 181, 254, 1)'; // blue
          if (item.Occupancy < 0.25) return 'rgba(67, 160, 71, 1)'; // green
          if (item.Occupancy < 0.5) return 'rgba(240, 255, 0, 0.8)'; // yellow
          return 'rgba(240, 52, 52, 0.8)'; // red
        case 'darkModus':
          if (item.Occupancy < 0.25) return true;
          if (item.Occupancy < 0.5) return false;
          return true;
        default:
          return false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.split('-');
      return `${day}-${month}-${year}`;
    },
    eventDetailBack() {
      this.$emit('back');
    },
    /**
     * to create user Object
     * @returns {{
     * Email: any,
     * Nr: string,
     * PhoneNumber: string,
     * Street: string,
     * PostalCode: *,
     * Name: string}}
     */
    getUserInfo() {
      return {
        Name: `${this.userObject.firstName} ${this.userObject.lastName}` || '',
        Email: this.userObject.email || '',
        PhoneNumber: this.userObject.phoneNo || '',
        Street: this.userObject.street || '',
        Nr: this.userObject.houseNo || '',
        PostalCode: this.userObject.postalCode || '',
        City: this.userObject.city || '',
        Country: this.userObject.country || '',
        CountryCode: this.userObject.countryCode || '',
        Latitude:
          this.userObject.latitude == null ? -200 : this.userObject.latitude,
        Longitude:
          this.userObject.longitude == null ? -200 : this.userObject.longitude,
      };
    },
    getGeocodeLocation() {
      if (this.userObject.countryCode !== 'NL') return;
      this.noConfidenceInAddress = false;
      this.showCityAndStreet = false;
      this.editCityAndStreet = false;
      this.userObject.street = '';
      this.userObject.city = '';
      if (
        this.userObject.postalCode.length !== 0 &&
        this.userObject.houseNo.length !== 0
      ) {
        this.geocodeAddress();
      }
    },
    geocodeAddress() {
      // Get gps location based on user's address info if Delivery needed
      this.isGeocoding = false;
      this.loadingAddress = true;

      if (this.userObject.latitude == null) {
        this.userObject.latitude = -200;
      }
      if (this.userObject.longitude == null) {
        this.userObject.longitude = -200;
      }

      // Start geocoding
      this.isGeocoding = true;
      const userAddressObj = this.getUserInfo();
      forwardGeocoding(
        userAddressObj,
        (result) => {
          // Geocoding finished successfully
          if (result.Latitude != null) {
            this.userObject.latitude = result.Latitude;
          }
          this.userObject.city = result.City;
          this.userObject.street = result.Street;
          this.loadingAddress = false;
          if (result.Longitude != null) {
            this.userObject.longitude = result.Longitude;
          }
          if (result.Confident) {
            this.showCityAndStreet = true;
            this.editCityAndStreet = false;
            this.noConfidenceInAddress = false;
          } else {
            this.showCityAndStreet = false;
            this.editCityAndStreet = true;
            this.noConfidenceInAddress = true;
          }
          this.isGeocoding = false;
        },
        () => {
          // Geocoding finished with error
          this.isGeocoding = false;
        }
      );
    },
    /**
     * function to reset the validation of the form to get no red text
     * when you remove the text and press save
     */
    resetCouponForm() {
      this.$refs.formRecipient.resetValidation();
    },
    editCityAndStreetData() {
      this.showCityAndStreet = false;
      this.editCityAndStreet = true;
    },
    getShoppingCartReservations() {
      const cartOrders = this.$store.getters['shoppingCart/orders'];
      const reservations = {};

      if (cartOrders != null) {
        cartOrders.forEach((reservation) => {
          reservations[reservation.EventId] = [reservation.OrderId];
        });
      }

      return reservations;
    },
    getTicketUnitPrice(ticket) {
      if (this.isProfUser) {
        return ticket.Price - ticket.VatPrice;
      }
      return ticket.Price;
    },
    getPriceSuffix() {
      if (this.isProfUser) {
        return this.$translate('EXCL_VAT');
      }
      return '';
    },
    getVATText,
    clearDeliveryFeeDetails() {
      this.deliveryNote = '';
      this.improvements = [];
      this.possibleStartDateList = [];
      this.startDate = '';
      this.listOfStartTimes = [];
      this.listOfTickets = {};
    },
    onDeliverySelectionChange() {
      this.clearDeliveryFeeDetails();
      this.parseDeliveryFeeDetails();
    },
    parseDeliveryFeeDetails() {
      if (this.selectedDeliveryFeeItem.DeliveryPossible) {
        this.deliveryText = this.getDeliveryFeeText(
          this.selectedDeliveryFeeItem
        );
        this.deliveryAlerts = [
          {
            type: 'info',
            text: this.deliveryText,
          },
        ];
        this.deliveryError = false;
        this.orderType.EventId = this.selectedDeliveryFeeItem.EventId;
        this.deliveryNote = this.selectedDeliveryFeeItem.Note;
        this.improvements = this.selectedDeliveryFeeItem.ResultSet.Improvements;
        this.getStartDatesSuccessfull(
          this.selectedDeliveryFeeItem.DateTimeInfo
        );
      } else {
        if (
          this.selectedDeliveryFeeItem.ResultSet != null &&
          this.selectedDeliveryFeeItem.ResultSet.AdditonalOrderValueForDelivery
        ) {
          this.deliveryText = this.$translate(
            'DELIVERY_NO_ENOUGH_PRODUCTS'
          ).replace(
            'price',
            this.formatter.format(
              this.selectedDeliveryFeeItem.ResultSet
                .AdditonalOrderValueForDelivery
            )
          );
        } else {
          this.deliveryText = this.$translate('NO_DELIVERY_FOR_POSTAL_CODE');
        }
        this.deliveryAlerts = [
          {
            type: 'error',
            text: this.deliveryText,
          },
        ];
        this.deliveryError = true;
      }
    },
    isObject(item) {
      return typeof item === 'object';
    },
    getDeliveryPickupSubtitle(item) {
      if (item.Type !== 'PickUp' || item.PickUpFee == null) {
        return '';
      }

      const pickupFeeObj = new PickupFeeJS(item.EventId);
      pickupFeeObj.updateWithRawData(item.PickUpFee);
      return pickupFeeObj.orderValueInfoText();
    },
    getPickupFee() {
      if (!this.isPickup) {
        this.pickupFee = null;
        return;
      }

      this.pickupFee = new PickupFeeJS(this.orderType.EventId);
      this.pickupFee.load(this.getShoppingCartReservations());
    },
  },
};
</script>

<style scoped>
.ticketAmount {
  align-self: center;
}
.error-message {
  color: red;
}
.disabled-text {
  color: #000000;
  opacity: 0.2;
}
</style>
